<div class="dist">
  <mat-progress-bar *ngIf="loading || busy" mode="indeterminate"></mat-progress-bar>

  <div *ngIf="error" class="dist__error">
    <div>{{ error }}</div>
    <div *ngIf="unassignedPropertyIds.length" class="dist__error-links">
      Edit property:
      <a *ngFor="let id of unassignedPropertyIds"
         [routerLink]="['/app/search/property', id]"
         class="dist__error-link">#{{ id }}</a>
    </div>
  </div>

  <!-- Generate (manual / ops) -->
  <mat-card class="dist__card">
    <h2 class="dist__title"><mat-icon>add_chart</mat-icon> Generate distribution</h2>
    <p class="dist__muted">Recurring windows are generated automatically by the schedules
      configured under Administrative ▸ Distributions. Use this only for an ad-hoc run.</p>
    <form [formGroup]="form" (ngSubmit)="generate()" class="dist__form">
      <mat-form-field appearance="outline">
        <mat-label>Date range</mat-label>
        <mat-date-range-input [rangePicker]="genPicker">
          <input matStartDate placeholder="Start date" formControlName="startDate">
          <input matEndDate placeholder="End date" formControlName="endDate">
        </mat-date-range-input>
        <mat-datepicker-toggle matIconSuffix [for]="genPicker"></mat-datepicker-toggle>
        <mat-date-range-picker #genPicker></mat-date-range-picker>
      </mat-form-field>
      <mat-form-field appearance="outline" class="dist__period">
        <mat-label>Period label</mat-label>
        <input matInput formControlName="periodLabel" placeholder="December 2025 (1-15)">
      </mat-form-field>
      <button mat-flat-button color="primary" type="submit" [disabled]="busy">Generate</button>
    </form>
  </mat-card>

  <!-- Runs list -->
  <mat-card class="dist__card">
    <h2 class="dist__title"><mat-icon>pie_chart</mat-icon> Distribution runs</h2>
    <table mat-table [dataSource]="runs" class="dist__table" *ngIf="runs.length; else empty">
      <ng-container matColumnDef="periodLabel">
        <th mat-header-cell *matHeaderCellDef>Period</th>
        <td mat-cell *matCellDef="let r">{{ r.periodLabel }}</td>
      </ng-container>
      <ng-container matColumnDef="window">
        <th mat-header-cell *matHeaderCellDef>Window</th>
        <td mat-cell *matCellDef="let r">{{ r.startDate }} &rarr; {{ r.endDate }}</td>
      </ng-container>
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>Status</th>
        <td mat-cell *matCellDef="let r"><span class="badge badge--{{ r.status }}">{{ r.status }}</span></td>
      </ng-container>
      <ng-container matColumnDef="total">
        <th mat-header-cell *matHeaderCellDef>Total to distribute</th>
        <td mat-cell *matCellDef="let r">{{ r.totalToDistribute | currency }}</td>
      </ng-container>
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let r">
          <button mat-button color="primary" (click)="select(r)">View</button>
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="['periodLabel','window','status','total','actions']"></tr>
      <tr mat-row *matRowDef="let row; columns: ['periodLabel','window','status','total','actions']"></tr>
    </table>
    <ng-template #empty><p class="dist__muted">No distribution runs yet.</p></ng-template>
  </mat-card>

  <!-- Detail -->
  <mat-card class="dist__card" *ngIf="selected as s" #detailCard>
    <div class="dist__detailhead">
      <h2 class="dist__title"><mat-icon>receipt_long</mat-icon> {{ s.periodLabel }}
        <span class="badge badge--{{ s.status }}">{{ s.status }}</span>
      </h2>
      <div class="dist__actions">
        <button mat-stroked-button (click)="downloadSummary()"><mat-icon>picture_as_pdf</mat-icon> Summary</button>
        <button mat-stroked-button (click)="downloadCollector()"><mat-icon>picture_as_pdf</mat-icon> Collector</button>
        <button mat-stroked-button *ngIf="canRerun(s)" [disabled]="busy" (click)="rerun()">Rerun</button>
        <button mat-stroked-button color="warn" *ngIf="canDelete(s)" [disabled]="busy" (click)="remove()"><mat-icon>delete</mat-icon> Delete</button>
        <button mat-flat-button color="primary" *ngIf="canApprove(s)" [disabled]="busy" (click)="approve()">Approve</button>
        <button mat-flat-button color="accent" *ngIf="canFinalize(s)" [disabled]="busy" (click)="finalize()">Finalize</button>
      </div>
    </div>

    <table mat-table [dataSource]="s.lines || []" class="dist__table" *ngIf="s.lines?.length; else noLines">
      <ng-container matColumnDef="jurisdictionName">
        <th mat-header-cell *matHeaderCellDef>Jurisdiction</th>
        <td mat-cell *matCellDef="let l">{{ l.jurisdictionName }}</td>
        <td mat-footer-cell *matFooterCellDef>Total</td>
      </ng-container>
      <ng-container matColumnDef="grossReceipts">
        <th mat-header-cell *matHeaderCellDef>Gross</th>
        <td mat-cell *matCellDef="let l">{{ l.grossReceipts | currency }}</td>
        <td mat-footer-cell *matFooterCellDef>{{ total(s.lines, 'grossReceipts') | currency }}</td>
      </ng-container>
      <ng-container matColumnDef="exemptReceipts">
        <th mat-header-cell *matHeaderCellDef>Exempt</th>
        <td mat-cell *matCellDef="let l">{{ l.exemptReceipts | currency }}</td>
        <td mat-footer-cell *matFooterCellDef>{{ total(s.lines, 'exemptReceipts') | currency }}</td>
      </ng-container>
      <ng-container matColumnDef="taxableReceipts">
        <th mat-header-cell *matHeaderCellDef>Taxable</th>
        <td mat-cell *matCellDef="let l">{{ l.taxableReceipts | currency }}</td>
        <td mat-footer-cell *matFooterCellDef>{{ total(s.lines, 'taxableReceipts') | currency }}</td>
      </ng-container>
      <ng-container matColumnDef="totalTaxCollected">
        <th mat-header-cell *matHeaderCellDef>Tax</th>
        <td mat-cell *matCellDef="let l">{{ l.totalTaxCollected | currency }}</td>
        <td mat-footer-cell *matFooterCellDef>{{ total(s.lines, 'totalTaxCollected') | currency }}</td>
      </ng-container>
      <ng-container matColumnDef="sumAllowance">
        <th mat-header-cell *matHeaderCellDef>Allowance</th>
        <td mat-cell *matCellDef="let l">{{ l.sumAllowance | currency }}</td>
        <td mat-footer-cell *matFooterCellDef>{{ total(s.lines, 'sumAllowance') | currency }}</td>
      </ng-container>
      <ng-container matColumnDef="sumPenaltyAmount">
        <th mat-header-cell *matHeaderCellDef>Penalty</th>
        <td mat-cell *matCellDef="let l">{{ l.sumPenaltyAmount | currency }}</td>
        <td mat-footer-cell *matFooterCellDef>{{ total(s.lines, 'sumPenaltyAmount') | currency }}</td>
      </ng-container>
      <ng-container matColumnDef="sumInterestAmount">
        <th mat-header-cell *matHeaderCellDef>Interest</th>
        <td mat-cell *matCellDef="let l">{{ l.sumInterestAmount | currency }}</td>
        <td mat-footer-cell *matFooterCellDef>{{ total(s.lines, 'sumInterestAmount') | currency }}</td>
      </ng-container>
      <ng-container matColumnDef="sumCollectionCommission">
        <th mat-header-cell *matHeaderCellDef>Commission</th>
        <td mat-cell *matCellDef="let l">{{ l.sumCollectionCommission | currency }}</td>
        <td mat-footer-cell *matFooterCellDef>{{ total(s.lines, 'sumCollectionCommission') | currency }}</td>
      </ng-container>
      <ng-container matColumnDef="distributionAmount">
        <th mat-header-cell *matHeaderCellDef>Net to County Fund</th>
        <td mat-cell *matCellDef="let l">{{ l.distributionAmount | currency }}</td>
        <td mat-footer-cell *matFooterCellDef>{{ total(s.lines, 'distributionAmount') | currency }}</td>
      </ng-container>
      <ng-container matColumnDef="retained">
        <th mat-header-cell *matHeaderCellDef>Collector Retained</th>
        <td mat-cell *matCellDef="let l">{{ retained(l) | currency }}</td>
        <td mat-footer-cell *matFooterCellDef>{{ total(s.lines, 'retained') | currency }}</td>
      </ng-container>
      <ng-container matColumnDef="collected">
        <th mat-header-cell *matHeaderCellDef>Total Collected</th>
        <td mat-cell *matCellDef="let l">{{ collected(l) | currency }}</td>
        <td mat-footer-cell *matFooterCellDef>{{ total(s.lines, 'collected') | currency }}</td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="lineColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: lineColumns"></tr>
      <tr mat-footer-row *matFooterRowDef="lineColumns" class="dist__total"></tr>
    </table>
    <ng-template #noLines><p class="dist__muted">No jurisdiction lines in this run.</p></ng-template>
  </mat-card>
</div>
